/**
 * Geolocation helper.
 *
 * iter-108: used by the hour-log clock-in / clock-out flow to stamp
 * each punch with GPS coordinates. ``requestLocationPermission`` is
 * called once on signup so the browser prompt fires at a natural
 * moment (rather than the first time a user hits Clock-in).
 */

const PERM_KEY = "planflow.geolocation.permissionPromptedAt";

/**
 * Politely prompt the browser for geolocation access. Resolves with
 * the first reading on success, or null on denial / unsupported.
 *
 * We never throw — geolocation is best-effort. Hour-log entries will
 * still be created without coordinates if the user denies.
 */
export async function requestLocationPermission({ silent = false } = {}) {
    try {
        if (!("geolocation" in navigator)) return null;
        return await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    try { localStorage.setItem(PERM_KEY, new Date().toISOString()); } catch { /* ignore */ }
                    resolve({
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                        accuracy_m: pos.coords.accuracy,
                        ts: new Date(pos.timestamp || Date.now()).toISOString(),
                    });
                },
                () => {
                    if (!silent) {
                        try { localStorage.setItem(PERM_KEY, new Date().toISOString()); } catch { /* ignore */ }
                    }
                    resolve(null);
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 60_000 },
            );
        });
    } catch {
        return null;
    }
}

/**
 * One-shot getter for the clock punch. Same as requestLocationPermission
 * but always silent (we don't care if the user denies — entry still goes
 * through, just without GPS).
 */
export function getCurrentLocation() {
    return requestLocationPermission({ silent: true });
}

export function hasPromptedGeolocation() {
    try { return !!localStorage.getItem(PERM_KEY); } catch { return false; }
}
